import React, { useState, useRef } from 'react';
import '../WidgetEditTypes/TopWidgetEdit.css';
import FontSelector from './FontSelector';
import InlineTextEffects from './InlineTextEffects';
import ButtonEffectsSelector from './ButtonEffectsSelector';
import ToggleSwitch from './ToggleSwitch';
import SimpleRichTextEditor from './SimpleRichTextEditor'; // ✅ Neuer einfacher Rich Text Editor
import { useFont } from '../../Design/FontContext'; // ✅ Font Context für globale Fonts

function TextInputSection({ textConfig, editData, handleInputChange, showFontSelector = false, showTextEffects = false, showButtonEffects = false }) {
    const [expandedItems, setExpandedItems] = useState({});
    const containerRefs = useRef({});
    const fonts = useFont(); // ✅ Font Context für globale Fonts

    const toggleExpanded = (fieldName) => {
        setExpandedItems(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    const hasControls = (textItem) => {
        return (showFontSelector && textItem.fontField) || 
               (showTextEffects && textItem.effectField) || 
               (showButtonEffects && textItem.buttonEffectField);
    };
    
    return (
        <div className="clean-text-inputs">
            {textConfig.map((textItem, index) => (
                <div 
                    className={`clean-text-item ${index !== textConfig.length - 1 ? 'has-separator' : ''}`}
                    key={index}
                    ref={el => containerRefs.current[textItem.fieldName] = el}
                >
                    <div className="clean-text-header">
                        <div className="clean-label-row">
                            <label htmlFor={textItem.fieldName} className="clean-text-label">{textItem.label}</label>
                            <div className="clean-controls-row">
                                {/* Show/Hide Toggle */}
                                <ToggleSwitch
                                    fieldName={textItem.showField || `show${textItem.fieldName.charAt(0).toUpperCase() + textItem.fieldName.slice(1)}`}
                                    checked={editData[textItem.showField || `show${textItem.fieldName.charAt(0).toUpperCase() + textItem.fieldName.slice(1)}`] !== false}
                                    onChange={handleInputChange}
                                    size="small"
                                    color="#5C80BC"
                                    showLabel={false}
                                />
                                {/* Font & Effects Toggle */}
                                {hasControls(textItem) && (
                                    <button 
                                        type="button"
                                        className={`clean-toggle-btn ${expandedItems[textItem.fieldName] ? 'expanded' : ''}`}
                                        onClick={() => toggleExpanded(textItem.fieldName)}
                                        title="Font & Effects Settings"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                            <path 
                                                d="M4 6L8 10L12 6" 
                                                stroke="currentColor" 
                                                strokeWidth="2" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                        {expandedItems[textItem.fieldName] && (
                            <div className="clean-controls-expanded">
                                {showFontSelector && textItem.fontField && (
                                    <FontSelector
                                        label=""
                                        fieldName={textItem.fontField}
                                        currentFont={editData[textItem.fontField]}
                                        onChange={handleInputChange}
                                        fontType={textItem.fontType || 'big'}
                                        containerRef={containerRefs.current[textItem.fieldName]}
                                    />
                                )}
                                {showTextEffects && textItem.effectField && (
                                    <InlineTextEffects
                                        fieldName={textItem.effectField}
                                        currentEffect={editData[textItem.effectField]}
                                        onChange={handleInputChange}
                                    />
                                )}
                                {showButtonEffects && textItem.buttonEffectField && (
                                    <ButtonEffectsSelector
                                        fieldName={textItem.buttonEffectField}
                                        currentEffect={editData[textItem.buttonEffectField]}
                                        onChange={handleInputChange}
                                        containerRef={containerRefs.current[textItem.fieldName]}
                                    />
                                )}
                                {/* ✅ MARGIN CONTROLS - Nur wenn marginTopField oder marginBottomField definiert sind */}
                                {(textItem.marginTopField || textItem.marginBottomField) && (
                                    <div className="margin-controls-section">
                                        <div className="margin-controls-header">
                                            <span className="margin-controls-label">Spacing</span>
                                        </div>
                                        <div className="margin-controls-grid">
                                            {textItem.marginTopField && (
                                                <div className="margin-control-item">
                                                    <label className="margin-control-label">Margin Top</label>
                                                    <div className="margin-slider-container">
                                                        <input
                                                            type="range"
                                                            name={textItem.marginTopField}
                                                            min="-50"
                                                            max="50"
                                                            step="5"
                                                            value={editData[textItem.marginTopField] ?? 0}
                                                            onChange={handleInputChange}
                                                            className="margin-slider"
                                                        />
                                                        <span className="margin-value">
                                                            {editData[textItem.marginTopField] ?? 0}px
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            {textItem.marginBottomField && (
                                                <div className="margin-control-item">
                                                    <label className="margin-control-label">Margin Bottom</label>
                                                    <div className="margin-slider-container">
                                                        <input
                                                            type="range"
                                                            name={textItem.marginBottomField}
                                                            min="-50"
                                                            max="50"
                                                            step="5"
                                                            value={editData[textItem.marginBottomField] ?? 0}
                                                            onChange={handleInputChange}
                                                            className="margin-slider"
                                                        />
                                                        <span className="margin-value">
                                                            {editData[textItem.marginBottomField] ?? 0}px
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* Input Field - Only show if toggle is enabled */}
                    {editData[textItem.showField || `show${textItem.fieldName.charAt(0).toUpperCase() + textItem.fieldName.slice(1)}`] !== false && (
                        <div className="clean-input-field">
                            {textItem.type === 'richtext' ? (
                                <SimpleRichTextEditor
                                    label=""
                                    fieldName={textItem.fieldName}
                                    value={editData[textItem.fieldName] || ''}
                                    onChange={handleInputChange}
                                    placeholder={textItem.placeholder}
                                    height={textItem.height || '200px'}
                                    // ✅ NORMALE EDITOR-STYLING (nicht finale Widget-Größe)
                                    fontFamily={editData[textItem.fontField] || (textItem.fontType === 'big' ? fonts.globalBigFont : fonts.globalSmallFont)}
                                    fontSize="14px" // ✅ Normale Editor-Größe statt finale Widget-Größe
                                    fontWeight="400" // ✅ Normale Editor-Weight statt finale Widget-Weight
                                    textColor="#333333" // ✅ Normale Editor-Farbe für bessere Lesbarkeit
                                />
                            ) : textItem.type === 'textarea' ? (
                                <textarea
                                    id={textItem.fieldName}
                                    name={textItem.fieldName}
                                    value={editData[textItem.fieldName] || ''}
                                    onChange={handleInputChange}
                                    className="clean-textarea"
                                    placeholder={textItem.placeholder}
                                    rows={textItem.rows || 3}
                                />
                            ) : (
                                <input
                                    id={textItem.fieldName}
                                    type="text"
                                    name={textItem.fieldName}
                                    value={editData[textItem.fieldName] || ''}
                                    onChange={handleInputChange}
                                    className="clean-input"
                                    placeholder={textItem.placeholder}
                                />
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export default TextInputSection;
